<template>
    <v-sheet
        v-if="isVisible"
        color="transparent"
        :width="size"
        :height="size"
        class="notification-icon"
        :class="[cssClasses, iconProps.cssClasses]"
    >
        <v-icon :icon="iconProps.icon" :color="iconProps.iconColor" :size="size" class="notification-icon__icon" />
        <v-sheet v-if="iconProps.bgColor" :color="iconProps.bgColor" class="notification-icon__underlay" />
    </v-sheet>
</template>

<script setup lang="ts">
import { NotificationMessageType } from '@/types/webapi';
import { computed } from 'vue';
import { kebabCase } from 'lodash-es';
import { getNotificationColor } from '../../helpers';

interface Props {
    modelValue?: NotificationMessageType;
    size?: number | string;
    alt?: boolean;
}

const props = withDefaults(defineProps<Props>(), { size: 18 });
const cssClasses = computed(() => [`notification-icon--${kebabCase(NotificationMessageType[props.modelValue || 0])}`]);
const isVisible = computed(() => !!props.modelValue && props.modelValue !== NotificationMessageType.Cancellation);
const iconProps = computed(() => {
    const color = getNotificationColor(props.modelValue);

    switch (props.modelValue) {
        case NotificationMessageType.BookingArrangement:
            return {
                icon: props.alt ? 'kdi-phone' : 'kdi-phone-circle',
                iconColor: props.alt ? color : 'green',
                bgColor: props.alt ? undefined : 'white'
            };

        case NotificationMessageType.OnlineBookingArrangement:
            return {
                icon: 'kdi-info-circle',
                iconColor: '#258752',
                bgColor: 'white'
            };

        case NotificationMessageType.Warning:
            return {
                icon: 'kdi-warning-circle',
                iconColor: color,
                bgColor: 'white'
            };

        case NotificationMessageType.Deviation:
            return {
                icon: 'kdi-alert-outline',
                iconColor: 'black',
                bgColor: color,
                cssClasses: 'notification-icon--triangle'
            };

        case NotificationMessageType.BookingConfirmation:
            return {
                icon: 'kdi-check-circle-outline',
                iconColor: 'green',
                bgColor: 'white'
            };

        default:
            return {
                icon: 'kdi-info-circle-outline',
                iconColor: color,
                bgColor: 'white'
            };
    }
});
</script>

<style lang="scss">
.notification-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    position: relative;

    &__underlay {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        clip-path: circle(35% at 50% 50%);

        .notification-icon--triangle & {
            border-radius: 0 !important;
            clip-path: polygon(15% 70%, 48% 18%, 52% 18%, 88% 78%, 78% 80%, 15% 80%);
        }
    }

    &__icon {
        color: currentColor;
        z-index: 1;
    }

    .notification-icon--overlap & + & {
        margin-left: -12px;
        z-index: 1;
    }
}

.notification-icon--overlap {
    display: flex;
    flex-wrap: nowrap;
}
</style>
